import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, throwError, Observable, BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CartService {

  url = environment.apiConfig.baseUrl; // use apiConfig baseUrl from environment
  // url = 'https://asa-back-dev.musea2.azure.ext.gm.com';
  
  private cartCountSubject = new BehaviorSubject<number>(0);
  public cartCount$ = this.cartCountSubject.asObservable();
  
  constructor(private _http: HttpClient) { }
  
  /**
   * Updates the cart count
   * @param count The new cart count
   */
  updateCartCount(count: number) {
    this.cartCountSubject.next(count);
  }
  
  /**
   * Gets the current cart count value
   */
  getCartCount(): number {
    return this.cartCountSubject.value;
  }
  
  /**
   * Fetches the cart items for a specific GMID from the backend.
   * @param gmid GMID of the user to fetch cart for
   * @returns An Observable containing an array of cart items.
  */
 getCartByGmid(gmid: any) {
   // Get fake cart data that matches gmid for testing purposes
    console.log('Fetching cart for GMID:', gmid);
    return this._http.get(this.url + '/enac/cart/gmid/' + gmid).pipe(
      map((res: any) => {
        // parse cliConfig
        res.forEach((item: any) => {
          if (typeof item.cliConfig === 'string') {
            item.cliConfig = JSON.parse(item.cliConfig);
          }
        });
        return res;
      }),
      tap((res: any) => {
        // Update cart count whenever cart is fetched
        this.updateCartCount(res.length);
      }),
      catchError((error) => {
        console.error('Error fetching cart:', error);
        // Optionally, you can show a user-friendly message or rethrow the error
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetches all cart items from the backend.
   * @returns An Observable containing an array of all cart items.
   */
  getAllCart() {
    return this._http.get(this.url + '/enac/cart/all').pipe(
      map((res: any) => {
        // parse cliConfig
        res.forEach((item: any) => {
          if (typeof item.cliConfig === 'string') {
            item.cliConfig = JSON.parse(item.cliConfig);
          }
        });
        return res;
      }),
      catchError((error) => {
        console.error('Error fetching all cart items:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Posts a new cart to the backend.
   * @param cart The cart object to be posted
   * @returns An Observable containing the posted cart or an error if the operation fails.
   */
  postObjToCart(cart: any) {
    // Logic to post the cart to the server or service
    cart.cliConfig = JSON.stringify(cart.cliConfig);
    console.log('Posting cart:', cart);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this._http.post(this.url + '/enac/cart', cart, { headers }).pipe(
      map((res:any) => res),
      tap(() => {
        // Increment cart count when item is added
        this.updateCartCount(this.getCartCount() + 1);
      })
    );
  }

  /**
   * Updates an existing cart in the backend.
   * @param cart The cart object to be updated
   * @returns An Observable containing the updated cart or an error if the operation fails.
   */
  updateCart(cart: any) {
    // Logic to update the cart on the server or service
    cart.cliConfig = JSON.stringify(cart.cliConfig);
    console.log('Updating cart:', cart);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this._http.put(this.url + '/enac/cart/edit', cart, { headers }).pipe(
      map((res:any) => res)
    );
  }

  /**
   * Deletes a cart item from the backend.
   * @param itemID itemID of the cart item to be deleted
   * @returns An Observable indicating the success or failure of the deletion operation.
   */
  deleteCartObj(itemID: any) {
    // Logic to delete the cart from the server or service
    console.log('Deleting cart:', itemID);
    return this._http.delete(this.url + '/enac/cart/' + itemID).pipe(
      map((res:any) => res)
    );
  }
}
